import { Injectable } from '@angular/core';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { constants } from '../../constants/constants';
import {
	ClientLoginDetails,
	ConsolidatedLoginBenefits,
	AdvisingAppointment,
	Participant,
	AdvisingSession
} from '@edassist/api';
import { AdvisingForm } from '../../models/advising/advising-form.interface';
import { ParticipantUtilityService } from '../participant/participant-utility.service';
import * as moment from 'moment-timezone';
import { ADVISING_CATEGORIES } from '../../enums/advising/advising-categories.enum';
import { TimezonePipe } from '@edassist-ui/timezone';

@Injectable({ providedIn: 'root' })
export class AdvisingUtilityService {
	static getParticipantId(): number {
		return LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER)
			?.participantId;
	}

	static getClientLoginDetails(): ClientLoginDetails {
		return LocalStorageService.getItem(
			constants.localStorageKeys.CLIENT_DETAILS
		)?.clientLoginDetails;
	}

	static getConsolidatedLoginBenefits(): Array<ConsolidatedLoginBenefits> {
		return LocalStorageService.getItem(
			constants.localStorageKeys.CLIENT_DETAILS
		)?.consolidatedLoginBenefits;
	}

	static getConsolidatedLoginUserId(): string {
		return LocalStorageService.getItem(
			constants.localStorageKeys.CLIENT_DETAILS
		)?.consolidatedLoginUserId;
	}

	static getCategoryCode(typeOfAdvising: number): string {
		switch (typeOfAdvising) {
			case ADVISING_CATEGORIES.SUCCESS_COACHING:
				return constants.ADVISING.CATEGORY_ID.SUCCESS_COACHING;
			case ADVISING_CATEGORIES.FINANCIAL:
				return constants.ADVISING.CATEGORY_ID.FINANCIAL;
			case ADVISING_CATEGORIES.ACADEMIC:
				return constants.ADVISING.CATEGORY_ID.ACADEMIC_POLICY;
			case ADVISING_CATEGORIES.POLICY:
				return constants.ADVISING.CATEGORY_ID.POLICY;
			default:
				return constants.ADVISING.CATEGORY_ID.ACADEMIC_POLICY;
		}
	}
	constructor(readonly timezonePipe: TimezonePipe) {}

	generateAppointmentDataTransferObject(
		advisingForm: AdvisingForm,
		productTypeId: number,
		participant: Participant,
		id?: string
	): AdvisingAppointment {
		if (id) {
			return {
				id,
				email:
					ParticipantUtilityService.getParticipantPreferredEmail(participant),
				phoneNumber: ParticipantUtilityService.getPreferredPhone(participant),
				secondaryPhoneNumber: '',
				service:
					advisingForm.advisingPhoneAppointment.availableCoachingSlot.service,
				serviceId:
					advisingForm.advisingPhoneAppointment.availableCoachingSlot.serviceId,
				advisorId:
					advisingForm.advisingPhoneAppointment.availableCoachingSlot.advisorId,
				scheduledStart:
					advisingForm.advisingPhoneAppointment.availableCoachingSlot
						.scheduledStart,
				scheduledEnd:
					advisingForm.advisingPhoneAppointment.availableCoachingSlot
						.scheduledEnd
			};
		}

		const highestLevelOfEducation =
			advisingForm.advisingYourHistory.highestLevelOfEducation.value;

		return {
			advisorId:
				advisingForm.advisingPhoneAppointment.availableCoachingSlot.advisorId,
			anticipatedStartDate: moment(
				advisingForm.advisingPhoneAppointment.availableCoachingSlot
					.scheduledStart
			).format('MM/DD/YYYY'),
			category: advisingForm.advisingTypeOfHelp.reasonForAdvising,
			productTypeId,
			currentlyEnrolledInUnivProgram:
				advisingForm.advisingYourHistory.currentEnrolledProgram.value || false,
			highestLevelOfEducation,
			highestLevelOfEducationEarned: highestLevelOfEducation,
			formattedEnd:
				advisingForm.advisingPhoneAppointment.availableCoachingSlot
					.formattedEnd,
			formattedStart:
				advisingForm.advisingPhoneAppointment.availableCoachingSlot
					.formattedStart,
			reasonForAdvising:
				advisingForm.advisingPhoneAppointment.reasonForAdvising,
			isReasonForAdvising: AdvisingUtilityService.getCategoryCode(
				advisingForm.advisingTypeOfHelp.reasonForAdvising
			),
			scheduledEnd:
				advisingForm.advisingPhoneAppointment.availableCoachingSlot
					.scheduledEnd,
			scheduledStart:
				advisingForm.advisingPhoneAppointment.availableCoachingSlot
					.scheduledStart,
			selectedSlot: JSON.stringify(
				advisingForm.advisingPhoneAppointment.availableCoachingSlot
			),
			service:
				advisingForm.advisingPhoneAppointment.availableCoachingSlot.service,
			serviceId:
				advisingForm.advisingPhoneAppointment.availableCoachingSlot.serviceId,
			email:
				ParticipantUtilityService.getParticipantPreferredEmail(participant),
			phoneNumber: ParticipantUtilityService.getPreferredPhone(participant),
			// Needed for call to go through, even though not asked on new ui
			currentEmpStatus: 0,
			currentlyInProcessOfPayingBackStudentLoans: true,
			currentLoanRepayment: 0,
			typeOfDegree: 0,
			noOfYearsInCurrentPosition: 0,
			schoolType: 0,
			reasonExpertGuidance: 0,
			reasonForAdvisingSession: 0,
			doUCurrentlyHaveStudentLoans: 0,
			whereAreYouInProcess: 2,
			yearsOfProfessionalExp: 0,
			yearsWithCurrentEmployer: 0,
			levelOfEducationSeeking: 0,
			numberOfYearsAtCompany: 0,
			secondaryPhoneNumber: '',
			totalLoanDebt: 0,
			typeOfStudentLoan: ''
		};
	}

	formatAndFilterScheduledAdvisingSessions(
		advisingSessions: Array<AdvisingSession>
	): Array<AdvisingSession> {
		const advisingSessionsItems = advisingSessions
			.map((advisingSession) => {
				advisingSession.appointmentTimeZone = this.timezonePipe.transform(
					new Date(advisingSession.appointment),
					moment.tz.guess(),
					'MMM D, YYYY - h:mm A z'
				);

				return advisingSession;
			})
			.filter(
				(advisingSession) =>
					new Date(advisingSession.appointment).getTime() >
						new Date().getTime() &&
					advisingSession.statusName ===
						constants.TUITION.APPOINTMENT_STATUS.SCHEDULED
			);

		return advisingSessionsItems;
	}
	formatAndFilterPastAdvisingSessions(
		advisingSessions: Array<AdvisingSession>
	): Array<AdvisingSession> {
		const advisingPastSessionsItems = advisingSessions
			.map((advisingSession) => {
				advisingSession.appointmentTimeZone = this.timezonePipe.transform(
					new Date(advisingSession.appointment),
					moment.tz.guess(),
					'MMM D, YYYY - h:mm A z'
				);

				return advisingSession;
			})
			.filter(
				(advisingSession) =>
					new Date(advisingSession.appointment).getTime() <
						new Date().getTime() ||
					advisingSession.statusName ===
						constants.TUITION.APPOINTMENT_STATUS.CANCELED
			);

		return advisingPastSessionsItems;
	}

	isAdvisingAppointmentAvailable(
		advisingSessions: Array<AdvisingSession>,
		isCoachingEnabled: boolean
	): boolean {
		return isCoachingEnabled && !!advisingSessions?.length;
	}
}
